import {
  type ClaimEventTypeValue,
  type FormFieldSchema,
  type FormStepSchema,
  type UserDraftClaim,
  type UserPolicyDetails,
  type UserSubmittedClaim,
  type WatchedFormValues,
} from '@/types';
import { getDraftClaimSummary } from '@/utils/claimSummary';
import { delay } from '@/utils/delay';
import { getIsCancelledError } from '@/utils/error';
import {
  createDraftClaim,
  fetchDraftClaim,
  flattenDraftClaimForForm,
  generateDraftClaimFromStepValues,
  getClaimEventStep,
  getClaimFinalStep,
  getClaimFormStepStart,
  getClaimIntroStep,
  getClaimTotalSteps,
  saveDraftClaim,
  submitDraftClaim,
} from '@/utils/newClaim';
import { fetchPolicyWithNumber } from '@/utils/policies';
import sanitized from 'sanitized';
import { useShallow } from 'zustand/react/shallow';
import type { User } from '../authStore';
import { createWithDevtools } from '../utils';
import { getClaimDocumentsState, selectCompletedClaimDocuments } from './claimDocumentsStore';

type ClaimFormStepName = FormStepSchema['name'];

type ClaimFormFieldName = FormFieldSchema['name'];

type ClaimFormStepValues = Record<ClaimFormStepName, ClaimFormFieldValues>;

type ClaimFormFieldValues = Record<ClaimFormFieldName, string | number | string[] | undefined>;

interface ClaimStepsStateProps {
  error: Error | null;
  hasError: boolean;
  isLoading: boolean;
  lastUpdated: number;
  currentStep: number;
  totalSteps: number;
  eventType?: ClaimEventTypeValue;
  formStep?: {
    isValid: boolean;
    values: ClaimFormStepValues;
  };
  claimPolicy?: UserPolicyDetails;
  draftClaim?: UserDraftClaim | UserSubmittedClaim;
  claimUser?: User;
}

interface ClaimStepsStateActions {
  resetAllSteps: () => void;
  setTotalSteps: (totalSteps: number) => void;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  goToStep: (stepNumber: number) => void;
  setClaimUser: (claimUser: User) => void;
  setIsLoading: (isLoading: boolean) => void;
  setEventType: (eventType?: ClaimEventTypeValue) => void;
  saveFormStepValues: (stepName: ClaimFormStepName, fieldValues: ClaimFormFieldValues) => void;
  setFormStepIsValid: (isValid: boolean) => void;
  fetchClaimPolicy: (policyNumber: string) => Promise<boolean>;
  resumeDraftClaim: (draftClaimId: string) => Promise<boolean>;
  submitDraftClaim: () => Promise<boolean>;
  createDraftClaim: () => Promise<boolean>;
  saveDraftClaim: () => Promise<boolean>;
}

type ClaimStepsState = ClaimStepsStateProps & ClaimStepsStateActions;

const initialStateProps: ClaimStepsStateProps = {
  error: null,
  hasError: false,
  isLoading: true,
  lastUpdated: Date.now(),
  currentStep: getClaimIntroStep(),
  totalSteps: getClaimTotalSteps(),
  formStep: undefined,
  eventType: undefined,
  claimPolicy: undefined,
  draftClaim: undefined,
  claimUser: undefined,
};

const useStore = createWithDevtools<ClaimStepsState>(
  (set, get) => ({
    ...initialStateProps,
    resetAllSteps: () => {
      set({ ...initialStateProps });
    },
    setTotalSteps: totalSteps => {
      set({ totalSteps });
    },
    goToNextStep: () => {
      set(({ currentStep }) => {
        if (currentStep < getClaimFinalStep()) {
          return { currentStep: currentStep + 1 };
        }

        return { currentStep };
      });
    },
    goToPrevStep: () => {
      set(({ currentStep }) => {
        if (currentStep > 0) {
          return { currentStep: currentStep - 1 };
        }

        return { currentStep };
      });
    },
    goToStep: stepNumber => {
      set(({ totalSteps, draftClaim }) => {
        const { draftClaimId } = draftClaim ?? {};

        if (draftClaimId) {
          if (stepNumber <= getClaimEventStep()) {
            // Can't go to earlier step, if resuming claim.
            return { currentStep: getClaimFormStepStart() };
          }
        }

        if (stepNumber > totalSteps) {
          return { currentStep: getClaimFinalStep() };
        }

        if (stepNumber <= 0) {
          return { currentStep: getClaimIntroStep() };
        }

        return { currentStep: stepNumber };
      });
    },
    setClaimUser: claimUser => {
      set({ claimUser });
    },
    setIsLoading: isLoading => {
      set({ isLoading });
    },
    setEventType: eventType => {
      set({ eventType });
    },
    setFormStepIsValid: isValid => {
      set(({ formStep = { isValid: false, values: {} } }) => ({
        formStep: { ...formStep, isValid },
      }));
    },
    saveFormStepValues: (stepName, fieldValues) => {
      set(({ formStep = { isValid: false, values: {} } }) => ({
        formStep: {
          ...formStep,
          values: { ...formStep.values, [stepName]: sanitized(fieldValues) },
        },
      }));
    },
    fetchClaimPolicy: async policyNumber => {
      set({ error: null, hasError: false, isLoading: true });
      const { data, error } = await fetchPolicyWithNumber(policyNumber);
      await delay(300); // Delay is for smoother UX/animations.

      // Ignore previous/pending requests that are cancelled.
      if (getIsCancelledError(error)) {
        return false;
      }

      if (error) {
        set({
          lastUpdated: Date.now(),
          claimPolicy: undefined,
          isLoading: false,
          hasError: true,
          error,
        });
        return false;
      }

      set({
        lastUpdated: Date.now(),
        claimPolicy: data,
        isLoading: false,
      });
      return true;
    },
    resumeDraftClaim: async draftClaimId => {
      set({ error: null, hasError: false, isLoading: true });
      const { claimUser, draftClaim: currentDraftClaim } = get();
      const { data: fetchedDraftClaim, error } = await fetchDraftClaim({
        draftClaimId,
        claimUser,
      });

      // Ignore previous/pending requests that are cancelled.
      if (getIsCancelledError(error)) {
        return false;
      }

      if (error || !fetchedDraftClaim) {
        set({
          draftClaim: currentDraftClaim,
          lastUpdated: Date.now(),
          isLoading: false,
          hasError: true,
          error,
        });
        return false;
      }

      const formStepValues = flattenDraftClaimForForm(fetchedDraftClaim);

      set({
        draftClaim: { ...currentDraftClaim, ...fetchedDraftClaim },
        formStep: { isValid: false, values: formStepValues },
        currentStep: getClaimFormStepStart(),
        lastUpdated: Date.now(),
        isLoading: false,
      });
      return true;
    },
    submitDraftClaim: async () => {
      const state = get();
      const { claimUser } = state;
      set({ error: null, hasError: false, isLoading: true });
      const draftClaim = (state.draftClaim ?? {}) as UserDraftClaim;

      // Submit this draft claim for policy (via BFF endpoint).
      const { data: submittedDetails, error } = await submitDraftClaim({
        draftClaim,
        claimUser,
      });

      // Ignore previous/pending requests that are cancelled.
      if (getIsCancelledError(error)) {
        return false;
      }

      // Check if BFF response data has valid submitted 'claimId'.
      const { claimId: submittedClaimId } = submittedDetails ?? {};
      if (error || !submittedClaimId) {
        set({
          draftClaim,
          lastUpdated: Date.now(),
          isLoading: false,
          hasError: true,
          error,
        });
        return false;
      }

      set({
        draftClaim: {
          ...draftClaim,
          submittedClaimId,
          draftClaimStatus: 'submitted',
        } as UserSubmittedClaim,
        // Redirect to the final step.
        currentStep: getClaimFinalStep(),
        lastUpdated: Date.now(),
        isLoading: false,
      });
      return true;
    },
    createDraftClaim: async () => {
      const { claimUser, claimPolicy } = get();
      set({ error: null, hasError: false, isLoading: true });
      const { policyNumber, type: policyType } = claimPolicy ?? {};
      const draftClaim = { policyNumber, policyType } as UserDraftClaim;

      // Create new draft claim for policy (via BFF endpoint).
      const { data: createdDraftClaim, error } = await createDraftClaim({
        draftClaim,
        claimUser,
      });

      // Ignore previous/pending requests that are cancelled.
      if (getIsCancelledError(error)) {
        return false;
      }

      // Check if BFF response data has a valid draft claim.
      if (error || !createdDraftClaim) {
        set({
          draftClaim,
          lastUpdated: Date.now(),
          isLoading: false,
          hasError: true,
          error,
        });
        return false;
      }

      set({
        draftClaim: { ...draftClaim, ...createdDraftClaim },
        lastUpdated: Date.now(),
        isLoading: false,
      });
      return true;
    },
    saveDraftClaim: async () => {
      const state = get();
      const { formStep, claimUser, claimPolicy } = state;
      set({ error: null, hasError: false, isLoading: true });
      const { travellers: travellersInPolicy = [] } = claimPolicy ?? {};
      const currentDraftClaim = (state.draftClaim ?? {}) as UserDraftClaim;
      const generatedDraftClaim = generateDraftClaimFromStepValues(formStep?.values);

      // Derive the draft claim summary with the latest step values and documents state.
      const summary = getDraftClaimSummary({
        claimPolicy,
        allFieldValues: selectClaimStepsAllFieldValues(state),
        completedDocuments: selectCompletedClaimDocuments(getClaimDocumentsState()),
        draftClaim: generatedDraftClaim,
      });

      const draftClaim = { ...currentDraftClaim, ...generatedDraftClaim, summary };

      /**
       * Match the time information of 'dateOfBirth' for listed travellers.
       * @TODO: Find a more effective way to extract/store this information.
       */
      if (draftClaim?.tripDetails?.impactedTravellers?.listedTravellers) {
        const { listedTravellers } = draftClaim.tripDetails.impactedTravellers;
        // Traveller names may be transformed, so its safer to compare them without any case.
        const transformTravellerName = (name: string) => name.toLowerCase().replaceAll(' ', '_');
        draftClaim.tripDetails.impactedTravellers.listedTravellers = [...listedTravellers].map(
          ({ firstName, lastName, dateOfBirth }) => {
            const travellerInPolicy = travellersInPolicy.find(
              traveller =>
                transformTravellerName(traveller.firstName) === transformTravellerName(firstName) &&
                transformTravellerName(traveller.lastName) === transformTravellerName(lastName),
            );

            return {
              dateOfBirth: travellerInPolicy?.dateOfBirth ?? dateOfBirth,
              firstName: travellerInPolicy?.firstName ?? firstName,
              lastName: travellerInPolicy?.lastName ?? lastName,
            };
          },
        );
      }

      // Save this draft claim for policy (via BFF endpoint).
      const { data: updatedDraftClaim, error } = await saveDraftClaim({
        draftClaim,
        claimUser,
      });

      // Ignore previous/pending requests that are cancelled.
      if (getIsCancelledError(error)) {
        return false;
      }

      // Check if BFF response data has a valid draft claim.
      if (error || !updatedDraftClaim) {
        set({
          draftClaim: currentDraftClaim,
          lastUpdated: Date.now(),
          isLoading: false,
          hasError: true,
          error,
        });
        return false;
      }

      set({
        draftClaim: { ...draftClaim, ...updatedDraftClaim },
        lastUpdated: Date.now(),
        isLoading: false,
      });
      return true;
    },
  }),
  'ClaimStepsStore',
);

// Memoized version of the store, using 'selector' with 'useShallow()'.
const useClaimStepsStore = <T>(selector: (state: ClaimStepsState) => T) => {
  return useStore(useShallow(selector));
};

const selectClaimStepsActions = (state: ClaimStepsState) => {
  return {
    resetAllSteps: state.resetAllSteps,
    setTotalSteps: state.setTotalSteps,
    goToNextStep: state.goToNextStep,
    goToPrevStep: state.goToPrevStep,
    goToStep: state.goToStep,
    setClaimUser: state.setClaimUser,
    setIsLoading: state.setIsLoading,
    setEventType: state.setEventType,
    saveFormStepValues: state.saveFormStepValues,
    setFormStepIsValid: state.setFormStepIsValid,
    fetchClaimPolicy: state.fetchClaimPolicy,
    resumeDraftClaim: state.resumeDraftClaim,
    submitDraftClaim: state.submitDraftClaim,
    createDraftClaim: state.createDraftClaim,
    saveDraftClaim: state.saveDraftClaim,
  };
};

const selectClaimStepsStatus = (state: ClaimStepsState) => {
  return {
    error: state.error,
    hasError: state.hasError,
    isLoading: state.isLoading,
    lastUpdated: state.lastUpdated,
  };
};

const selectClaimStepsCurrentStep = (state: ClaimStepsState) => {
  return state.currentStep;
};

const selectClaimStepsTotalSteps = (state: ClaimStepsState) => {
  return state.totalSteps;
};

const selectClaimStepsFormStep = (state: ClaimStepsState) => {
  return state.formStep;
};

const selectClaimStepsEventType = (state: ClaimStepsState) => {
  return state.eventType;
};

const selectClaimStepsClaimUser = (state: ClaimStepsState) => {
  return state.claimUser;
};

const selectClaimStepsClaimPolicy = (state: ClaimStepsState) => {
  return state.claimPolicy;
};

const selectClaimStepsDraftClaim = (state: ClaimStepsState) => {
  return state.draftClaim as UserDraftClaim | undefined;
};

const selectClaimStepsSubmittedClaim = (state: ClaimStepsState) => {
  if (state.draftClaim?.draftClaimStatus === 'submitted') {
    return state.draftClaim;
  }

  return undefined;
};

const selectClaimStepsDraftClaimId = (state: ClaimStepsState) => {
  return selectClaimStepsDraftClaim(state)?.draftClaimId;
};

const selectClaimStepsDraftSubmittedId = (state: ClaimStepsState) => {
  return selectClaimStepsSubmittedClaim(state)?.submittedClaimId;
};

const selectClaimStepsDraftClaimPolicyNumber = (state: ClaimStepsState) => {
  return selectClaimStepsDraftClaim(state)?.policyNumber;
};

const selectClaimStepsFormStepIsValid = (state: ClaimStepsState) => {
  return selectClaimStepsFormStep(state)?.isValid ?? false;
};

const selectClaimStepsFormStepValues =
  (stepName: ClaimFormStepName) => (state: ClaimStepsState) => {
    return selectClaimStepsFormStep(state)?.values[stepName];
  };

const selectClaimStepsAllFieldValues = (state: ClaimStepsState) => {
  const stepValues = selectClaimStepsFormStep(state)?.values ?? {};

  return Object.values(stepValues).reduce<WatchedFormValues>(
    (allFieldValues, fieldValues) => ({ ...allFieldValues, ...fieldValues }),
    {},
  );
};

export {
  selectClaimStepsActions,
  selectClaimStepsAllFieldValues,
  selectClaimStepsClaimPolicy,
  selectClaimStepsClaimUser,
  selectClaimStepsCurrentStep,
  selectClaimStepsDraftClaim,
  selectClaimStepsDraftClaimId,
  selectClaimStepsDraftClaimPolicyNumber,
  selectClaimStepsDraftSubmittedId,
  selectClaimStepsEventType,
  selectClaimStepsFormStep,
  selectClaimStepsFormStepIsValid,
  selectClaimStepsFormStepValues,
  selectClaimStepsStatus,
  selectClaimStepsTotalSteps,
  useClaimStepsStore,
};

export type {
  ClaimFormFieldValues,
  ClaimFormStepValues,
  ClaimStepsState,
  ClaimStepsStateActions,
  ClaimStepsStateProps,
};
